/*****************************************************
Naam: CustomerLogin.jsx
Versie: 2.0.0 - SENDWELL REDESIGN
Navy/cyan corporate stijl, scherpe hoeken
******************************************************/

import { useLanguage } from '../LanguageContext';
import TaalWisselaar from '../TaalWisselaar';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogIn, AlertCircle, User, Lock } from 'lucide-react';

const CustomerLogin = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ username: '', password: '', rememberMe: false });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [companyLogo, setCompanyLogo] = useState('');

  useEffect(() => {
    fetch('/api/customer/check-auth')
      .then(res => res.json())
      .then(data => { if (data.authenticated) navigate('/customer/dashboard'); })
      .catch(err => console.error('Auth check error:', err));
    const logo = localStorage.getItem('companyLogo');
    if (logo) setCompanyLogo(logo);
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const response = await fetch('/api/customer/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: formData.username, password: formData.password })
      });
      const data = await response.json();
      if (data.success) { navigate('/customer/dashboard'); } else { setError(typeof data.error === 'object' ? (data.error.message || 'Login failed') : (data.error || 'Login failed')); }
    } catch (err) {
      console.error('Login error:', err);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center"
      style={{ background: 'linear-gradient(135deg, #0f2233 0%, #1a364c 50%, #2a4a64 100%)' }}>
      <div className="absolute inset-0 pointer-events-none"
        style={{ backgroundImage: 'radial-gradient(circle at 75% 25%, rgba(194,230,239,0.06) 0%, transparent 50%)' }} />
      <div className="w-full max-w-md relative">
        {companyLogo && (
          <div className="flex justify-center mb-8">
            <img src={companyLogo} alt="Company Logo" className="h-20 object-contain" />
          </div>
        )}

        <div style={{ boxShadow: '0 12px 48px rgba(0,0,0,0.35)' }}>
          <div style={{ height: '4px', background: '#c2e6ef' }} />
          <div style={{ backgroundColor: '#ffffff', padding: '32px' }}>
            <div className="text-center mb-8">
              <h1 className="text-2xl font-bold mb-2" style={{ color: '#1a364c' }}>{t('klanten_portal')}</h1>
              <p style={{ color: '#94a3b8', fontSize: '14px' }}>{t('login_sub')}</p>
            </div>

            {error && (
              <div className="mb-6 p-4 flex items-start gap-3"
                style={{ backgroundColor: '#fee2e2', border: '1px solid #fecaca' }}>
                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#dc2626' }} />
                <p className="text-sm" style={{ color: '#dc2626' }}>{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: '#1a364c' }}>{t('gebruikersnaam')}</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: '#94a3b8' }} />
                  <input type="text" name="username" value={formData.username} onChange={handleChange}
                    required autoFocus className="w-full pl-10 pr-4 py-3 outline-none transition-all"
                    style={{ border: '1.5px solid #e2e8f0' }}
                    onFocus={(e) => { e.target.style.borderColor = '#c2e6ef'; e.target.style.boxShadow = '0 0 0 3px rgba(194,230,239,0.3)'; }}
                    onBlur={(e) => { e.target.style.borderColor = '#e2e8f0'; e.target.style.boxShadow = 'none'; }}
                    placeholder={t('login_gebruikersnaam')} />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: '#1a364c' }}>{t('login_wachtwoord')}</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: '#94a3b8' }} />
                  <input type="password" name="password" value={formData.password} onChange={handleChange}
                    required className="w-full pl-10 pr-4 py-3 outline-none transition-all"
                    style={{ border: '1.5px solid #e2e8f0' }}
                    onFocus={(e) => { e.target.style.borderColor = '#c2e6ef'; e.target.style.boxShadow = '0 0 0 3px rgba(194,230,239,0.3)'; }}
                    onBlur={(e) => { e.target.style.borderColor = '#e2e8f0'; e.target.style.boxShadow = 'none'; }}
                    placeholder="••••••••" />
                </div>
              </div>

              <div className="flex items-center">
                <input type="checkbox" name="rememberMe" id="rememberMe" checked={formData.rememberMe}
                  onChange={handleChange} className="w-4 h-4" style={{ accentColor: '#1a364c' }} />
                <label htmlFor="rememberMe" className="ml-2 text-sm" style={{ color: '#475569' }}>{t('login_onthoud')}</label>
              </div>

              <button type="submit" disabled={loading} className="w-full py-3 px-4 font-semibold text-white transition-all flex items-center justify-center gap-2"
                style={{
                  background: loading ? '#94a3b8' : '#1a364c',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  borderBottom: loading ? 'none' : '3px solid #c2e6ef',
                  border: 'none',
                }}>
                {loading ? (
                  <><div className="animate-spin h-5 w-5 border-b-2 border-white" style={{ borderRadius: '50%' }}></div><span>{t('inloggen_bezig')}</span></>
                ) : (
                  <><LogIn className="w-5 h-5" /><span>{t('inloggen')}</span></>
                )}
              </button>
              <div style={{marginTop:16,textAlign:'center'}}><TaalWisselaar stijl='login' /></div>
</form>

            <div className="mt-8 text-center">
              <p className="text-sm" style={{ color: '#64748b' }}>
                Problemen met inloggen?{' '}
                <a href="mailto:support@sendwell.nl" className="font-medium" style={{ color: '#1a364c' }}>{t('login_contact')}</a>
              </p>
            </div>
          </div>
        </div>

        <div className="mt-6 text-center">
          <a href="/" className="text-sm transition-colors" style={{ color: 'rgba(194,230,239,0.7)' }}>{t('login_admin')}</a>
        </div>
      </div>
    </div>
  );
};

export default CustomerLogin;
